import { HttpTransportType, HubConnection, HubConnectionBuilder, LogLevel } from "@microsoft/signalr";
import { AppConfig } from "../config/config";
import { Injectable } from "@angular/core";
import { BehaviorSubject, Subject } from "rxjs";

interface ConnectionInfo {
  connection: HubConnection;
  handlers: Map<string, (...args: any[]) => void>;
  subscriptions: Set<string>;
  isConnecting: boolean; // Prevent concurrent connection attempts
}

@Injectable({
  providedIn: 'root',
})
export class SignalRService {
  private url = this.config.setting['pathApi']

  public readonly connected$ = new BehaviorSubject<boolean>(false);
  public readonly disconnected$ = new Subject<Error | undefined>();
  private connections = new Map<string, ConnectionInfo>();

  constructor(private config: AppConfig) { }

  async startNewConnection(path: string): Promise<HubConnection> {
    const fullUrl = this.url + path;

    // Check if already connected or connecting
    const existing = this.connections.get(path);
    
    // If connected, return existing connection
    if (existing && existing.connection.state === 'Connected') {
      return existing.connection;
    }

    // If connecting, wait for it to complete (prevent concurrent attempts)
    if (existing && existing.isConnecting) {
      // Wait for connection to complete
      return new Promise((resolve, reject) => {
        const checkInterval = setInterval(() => {
          const current = this.connections.get(path);
          if (current && !current.isConnecting) {
            clearInterval(checkInterval);
            if (current.connection.state === 'Connected') {
              resolve(current.connection);
            } else {
              reject(new Error('Connection failed'));
            }
          }
        }, 100);
        
        // Timeout after 10 seconds
        setTimeout(() => {
          clearInterval(checkInterval);
          reject(new Error('Connection timeout'));
        }, 10000);
      });
    }

    // If there's a disconnected connection, clean it up first
    if (existing && !existing.isConnecting) {
      await this.stopConnection(path);
    }
    
    // Create connection info with isConnecting flag
    const connectionInfo: ConnectionInfo = {
      connection: null as any,
      handlers: new Map<string, (...args: any[]) => void>(),
      subscriptions: new Set<string>(),
      isConnecting: true,
    };
    this.connections.set(path, connectionInfo);

    try {
      const hubConnection = new HubConnectionBuilder()
        .withUrl(fullUrl, {
          skipNegotiation: true,
          transport: HttpTransportType.WebSockets,
        })
        .withAutomaticReconnect([0, 2000, 5000, 10000])
        .configureLogging(LogLevel.Information)
        .build();

      connectionInfo.connection = hubConnection;

      // Connection lifecycle handlers
      hubConnection.onreconnecting((error) => {
        this.connected$.next(false);
      });

      hubConnection.onreconnected(async (connectionId) => {
        this.connected$.next(true);
        
        // Re-subscribe to all groups
        const info = this.connections.get(path);
        if (info) {
          for (const deviceNumber of info.subscriptions) {
            try {
              await info.connection.invoke('SubscribeToDevice', deviceNumber);
            } catch (err) {
              console.error(`[SignalR] Failed to re-subscribe to device ${deviceNumber}:`, err);
            }
          }
        }
        
        // Re-register handlers
        for (const [event, callback] of connectionInfo.handlers.entries()) {
          hubConnection.on(event, callback);
        }
      });

      hubConnection.onclose((error) => {
        this.connected$.next(false);
        this.disconnected$.next(error);
        
        // Mark as not connecting anymore
        const info = this.connections.get(path);
        if (info) {
          info.isConnecting = false;
        }
      });

      await hubConnection.start();
      
      // Mark connection as established
      connectionInfo.isConnecting = false;
      this.connected$.next(true);
      
      return hubConnection;
    } catch (err) {
      console.error('[SignalR] Failed to connect:', err);
      // Clean up failed connection attempt
      connectionInfo.isConnecting = false;
      this.connections.delete(path);
      this.connected$.next(false);

      throw err;
    }
  }

  /**
   * Subscribe to device-specific updates
   * @param path Hub path (e.g., '/notification')
   * @param deviceNumber Device identifier
   */
  async subscribeToDevice(path: string, deviceNumber: string): Promise<void> {
    const info = this.connections.get(path);
    if (!info) {
      return;
    }

    // Wait if connection is still being established
    if (info.isConnecting) {
      await new Promise(resolve => setTimeout(resolve, 500));
      return this.subscribeToDevice(path, deviceNumber); // Retry
    }

    // Check if already subscribed
    if (info.subscriptions.has(deviceNumber)) {
      return;
    }

    try {
      await info.connection.invoke('SubscribeToDevice', deviceNumber);
      info.subscriptions.add(deviceNumber);
    } catch (err) {
      console.error(`[SignalR] Failed to subscribe to device ${deviceNumber}:`, err);
      throw err;
    }
  }

  /**
   * Unsubscribe from device-specific updates
   * @param path Hub path (e.g., '/notification')
   * @param deviceNumber Device identifier
   */
  async unsubscribeFromDevice(path: string, deviceNumber: string): Promise<void> {
    const info = this.connections.get(path);
    if (!info) {
      return;
    }

    if (!info.subscriptions.has(deviceNumber)) {
      return;
    }

    try {
      // Only invoke if connection is active
      if (info.connection.state === 'Connected') {
        await info.connection.invoke('UnsubscribeFromDevice', deviceNumber);
      }
      info.subscriptions.delete(deviceNumber);
    } catch (err) {
      console.error(`[SignalR] Failed to unsubscribe from device ${deviceNumber}:`, err);
      // Still remove from local tracking
      info.subscriptions.delete(deviceNumber);
    }
  }

  registerHandler(path: string, event: string, callback: (...args: any[]) => void) {
    const info = this.connections.get(path);
    if (!info) {
      return;
    }

    // Remove existing handler if it exists
    const existingCallback = info.handlers.get(event);
    if (existingCallback) {
      info.connection.off(event, existingCallback);
    }

    info.handlers.set(event, callback);
    info.connection.on(event, callback);
  }

  removeHandler(path: string, event: string) {
    const info = this.connections.get(path);
    if (!info) return;

    const callback = info.handlers.get(event);
    if (callback) {
      info.connection.off(event, callback);
      info.handlers.delete(event);
    }
  }

  async stopConnection(path: string) {
    const existing = this.connections.get(path);
    if (!existing) return;

    try {
      // Don't try to unsubscribe if connection is not active
      if (existing.connection.state === 'Connected') {
        // Unsubscribe from all devices
        for (const deviceNumber of existing.subscriptions) {
          await this.unsubscribeFromDevice(path, deviceNumber);
        }
      }

      // Remove all handlers
      for (const [event, callback] of existing.handlers.entries()) {
        existing.connection.off(event, callback);
      }
      
      await existing.connection.stop();
    } catch (err) {
      console.error(`[SignalR] Failed to stop connection for path: ${path}:`, err);
    } finally {
      this.connections.delete(path);
    }
  }

  async stopAllConnections() {
    const paths = Array.from(this.connections.keys());
    for (const path of paths) {
      await this.stopConnection(path);
    }
  }

  closeConnection(connection: HubConnection | undefined) {
    if (connection) {
      connection.stop();
    }
  }
}
